import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { Logo } from "./Logo";
import { useAuth } from "@/lib/auth";
import { CustomerSessionPill } from "@/components/CustomerOnboarding";
import { LayoutDashboard, Package, Plus, LogOut, UploadCloud, Users, Settings, Sparkles } from "lucide-react";

const navItem =
  "flex items-center gap-3 px-4 py-2.5 text-sm font-medium text-[#5C5C54] hover:text-[#1A1A18] hover:bg-[#F2F0EA] rounded-sm transition-colors";
const navActive = "text-[#1A1A18] bg-[#F2F0EA]";

export default function AdminLayout({ children }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();

  return (
    <div className="min-h-screen bg-[#FAFAF8] text-[#1A1A18] flex">
      {/* Sidebar */}
      <aside className="hidden md:flex md:flex-col w-64 border-r border-[#E8E6DF] bg-white">
        <div className="px-6 py-6 border-b border-[#E8E6DF]">
          <Logo size={32} />
        </div>
        <nav className="flex-1 p-4 space-y-1">
          <NavLink
            to="/admin"
            end
            className={({ isActive }) => `${navItem} ${isActive ? navActive : ""}`}
            data-testid="nav-dashboard-link"
          >
            <LayoutDashboard size={18} strokeWidth={1.5} />
            Dashboard
          </NavLink>
          <NavLink
            to="/admin/products"
            className={({ isActive }) => `${navItem} ${isActive ? navActive : ""}`}
            data-testid="nav-products-link"
          >
            <Package size={18} strokeWidth={1.5} />
            Products
          </NavLink>
          <NavLink
            to="/admin/products/new"
            className={({ isActive }) => `${navItem} ${isActive ? navActive : ""}`}
            data-testid="nav-new-product-link"
          >
            <Plus size={18} strokeWidth={1.5} />
            New Product
          </NavLink>
          <NavLink
            to="/admin/products/bulk"
            className={({ isActive }) => `${navItem} ${isActive ? navActive : ""}`}
            data-testid="nav-bulk-upload-link"
          >
            <UploadCloud size={18} strokeWidth={1.5} />
            Bulk Upload
          </NavLink>

          <div className="hed-divider my-3" />

          <NavLink
            to="/admin/ask"
            className={({ isActive }) => `${navItem} ${isActive ? navActive : ""}`}
            data-testid="nav-ask-heda-link"
          >
            <Sparkles size={18} strokeWidth={1.5} className="text-[#B5953B]" />
            Ask Heda AI
          </NavLink>

          <NavLink
            to="/admin/customers"
            className={({ isActive }) => `${navItem} ${isActive ? navActive : ""}`}
            data-testid="nav-customers-link"
          >
            <Users size={18} strokeWidth={1.5} />
            Customers
          </NavLink>

          <NavLink
            to="/admin/rebrand"
            className={({ isActive }) => `${navItem} ${isActive ? navActive : ""}`}
            data-testid="nav-rebrand-link"
          >
            <UploadCloud size={18} strokeWidth={1.5} />
            PDF Rebrand
          </NavLink>

          <div className="hed-divider my-3" />

          <NavLink
            to="/admin/settings"
            className={({ isActive }) => `${navItem} ${isActive ? navActive : ""}`}
            data-testid="nav-settings-link"
          >
            <Settings size={18} strokeWidth={1.5} />
            Settings
          </NavLink>
          {user?.role === "owner" && (
            <NavLink
              to="/admin/users"
              className={({ isActive }) => `${navItem} ${isActive ? navActive : ""}`}
              data-testid="nav-users-link"
            >
              <Users size={18} strokeWidth={1.5} />
              Team
            </NavLink>
          )}
        </nav>
        <div className="p-4 border-t border-[#E8E6DF]">
          <div className="text-xs text-[#5C5C54] mb-2 truncate">{user?.email}</div>
          <button
            data-testid="admin-logout-button"
            onClick={() => {
              logout();
              nav("/admin/login");
            }}
            className="w-full flex items-center justify-center gap-2 px-3 py-2 text-sm border border-[#E8E6DF] hover:bg-[#F2F0EA] rounded-sm transition-colors"
          >
            <LogOut size={14} strokeWidth={1.5} /> Sign out
          </button>
        </div>
      </aside>

      {/* Mobile top bar */}
      <div className="md:hidden fixed top-0 inset-x-0 z-40 bg-white border-b border-[#E8E6DF] flex items-center justify-between px-4 py-3" style={{ paddingTop: "max(0.75rem, env(safe-area-inset-top))" }}>
        <Logo size={32} variant="mark" />
        <span className="text-base font-bold text-[#1A1A18]">Hedaply</span>
        <button
          onClick={() => {
            logout();
            nav("/admin/login");
          }}
          className="text-sm text-[#5C5C54]"
          data-testid="admin-logout-button-mobile"
        >
          Sign out
        </button>
      </div>

      <main className="flex-1 md:ml-0 mt-16 md:mt-0 w-full min-w-0">
        <div className="w-full max-w-6xl mx-auto px-4 md:px-10 py-6 md:py-12">
          <div className="flex justify-end mb-4">
            <CustomerSessionPill />
          </div>
          {children}
        </div>
      </main>
    </div>
  );
}
